// Enhanced src/util/dynamodbService.js with FIXED coordinate handling
import { types as sdkTypes } from './sdkLoader';
import {
  searchDynamoDBListings as apiSearchDynamoDBListings,
  fetchDynamoDBListingsAPI,
} from './api';

const { LatLng: SDKLatLng, Money } = sdkTypes;

/**
 * Transform DynamoDB listing data to Sharetribe listing format
 */
export const transformDynamoDBToSharetribeListing = dynamoItem => {
  try {
    // Parse location data - USE ACTUAL COORDINATES FIRST!
    const location = dynamoItem.location;
    let geolocation = null;

    // 🔥 FIX: Use actual coordinates from the API if available
    if (location?.latitude && location?.longitude) {
      const lat = parseFloat(location.latitude);
      const lng = parseFloat(location.longitude);

      // Validate coordinates are reasonable
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        geolocation = new SDKLatLng(lat, lng);
      } 
    }

    // Fallback to hardcoded coordinates only if actual coordinates unavailable or invalid
    if (!geolocation && location?.destination) {
      const fallbackCoords = {
        arusha: { lat: -3.3869, lng: 36.683 },
        serengeti: { lat: -2.3333, lng: 34.8333 },
        ngorongoro: { lat: -3.2, lng: 35.5 },
        zanzibar: { lat: -6.1659, lng: 39.2026 },
        kilimanjaro: { lat: -3.0674, lng: 37.3556 },
        tarangire: { lat: -3.8333, lng: 35.85 },
        'lake manyara': { lat: -3.4833, lng: 35.8167 },
        'dar es salaam': { lat: -6.7924, lng: 39.2083 },
        'stone town': { lat: -6.1659, lng: 39.1925 },
        moshi: { lat: -3.35, lng: 37.3333 },
        athens: { lat: 37.9755, lng: 23.7348 },
        georgetown: { lat: 6.8013, lng: -58.1551 },
        paros: { lat: 37.0833, lng: 25.1333 },
        naxos: { lat: 37.1, lng: 25.3667 },
        dubai: { lat: 25.2048, lng: 55.2708 },
        paris: { lat: 48.8566, lng: 2.3522 },
        london: { lat: 51.5074, lng: -0.1278 },
        rome: { lat: 41.9028, lng: 12.4964 },
        barcelona: { lat: 41.3851, lng: 2.1734 },
        amsterdam: { lat: 52.3676, lng: 4.9041 },
        tokyo: { lat: 35.6762, lng: 139.6503 },
        'new york': { lat: 40.7128, lng: -74.006 },
        venice: { lat: 45.4408, lng: 12.3155 },
        santorini: { lat: 36.3932, lng: 25.4615 },
        istanbul: { lat: 41.0082, lng: 28.9784 },
        tamarindo: { lat: 10.30622, lng: -85.80283 },
        'costa rica': { lat: 9.7489, lng: -83.7534 },
        guanacaste: { lat: 10.6345, lng: -85.4432 },
      };

      const destinationKey = location.destination.toLowerCase();
      let coords = fallbackCoords[destinationKey];

      // Try partial matching if exact match not found
      if (!coords) {
        for (const [key, coordsValue] of Object.entries(fallbackCoords)) {
          if (destinationKey.includes(key) || key.includes(destinationKey)) {
            coords = coordsValue;
            break;
          }
        }
      }

      if (coords) {
        geolocation = new SDKLatLng(coords.lat, coords.lng);

      }
    }

    // Parse photo URLs
    const photoUrls = dynamoItem.photoUrls || [];
    const images = photoUrls.map((photoUrl, index) => ({
      id: `${dynamoItem.id}-img-${index}`,
      type: 'image',
      attributes: {
        variants: {
          'listing-card': {
            url: photoUrl,
            width: 400,
            height: 300,
          },
          'listing-card-2x': {
            url: photoUrl,
            width: 800,
            height: 600,
          },
        },
      },
    }));

    // Create main image from mainPhotoUrl or first photo
    const mainImage = dynamoItem.mainPhotoUrl
      ? {
          id: `${dynamoItem.id}-main-img`,
          type: 'image',
          attributes: {
            variants: {
              'listing-card': {
                url: dynamoItem.mainPhotoUrl,
                width: 400,
                height: 300,
              },
              'listing-card-2x': {
                url: dynamoItem.mainPhotoUrl,
                width: 800,
                height: 600,
              },
            },
          },
        }
      : images.length > 0
      ? images[0]
      : null;

    // Convert price to proper Money object - use actual price if available
    let price;

    try {
      const priceValue = parseFloat(dynamoItem.averagePrice || 0);
      const priceInCents = Math.round(priceValue * 100); // Convert to cents
      price = new Money(priceInCents, 'USD');
    } catch (moneyError) {
      price = new Money(0, 'USD'); // Fallback
    }

    // Enhanced public data with review and quality metrics
    const reviewCount = parseInt(dynamoItem.reviewCount || dynamoItem.totalRatingCount || 0);
    const rating = parseFloat(dynamoItem.rating || 0);
    const photoCount = parseInt(dynamoItem.photoCount || 0);

    const result = {
      id: {
        uuid: `dynamo-${dynamoItem.id}`,
        type: 'listing',
      },
      type: 'listing',
      attributes: {
        title: dynamoItem.title || 'Experience',
        description: dynamoItem.description || '',
        price,
        geolocation, // 🔥 This is the key - must be set correctly!
        state: 'published',
        publicData: {
          listingType: 'experience',
          transactionProcessAlias: 'default-booking',
          unitType: 'item',
          isDynamoDBListing: true,

          // Partner experience indicators
          isPartnerExperience: true,
          partneredWith: 'Viator',

          // Quality metrics for prioritization
          reviewCount,
          rating,
          stars: Math.round(rating),
          totalRatingCount: reviewCount,
          photoCount,

          // Review-based quality indicators
          hasHighReviews: reviewCount >= 50,
          hasExcellentRating: rating >= 4.5,
          qualityScore: Math.round(rating * 20 + Math.min(reviewCount / 10, 50)),

          // Promotion indicators based on quality
          promoted: reviewCount >= 100 && rating >= 4.0,
          featured: reviewCount >= 500 && rating >= 4.5,
          priority: reviewCount >= 1000 ? 'high' : reviewCount >= 100 ? 'medium' : 'normal',

          // Link and metadata
          affiliateLink: dynamoItem.affiliateLink,
          lastUpdated: dynamoItem.lastUpdated || dynamoItem.scraped_at,

          // Location data - include raw coordinates for debugging
          location: {
            destination: location?.destination,
            country: location?.country,
            city: location?.city,
            address: location?.address,
            rawLatitude: location?.latitude,
            rawLongitude: location?.longitude,
            hasCoordinates: !!(location?.latitude && location?.longitude),
          },
        },
      },
      images: mainImage ? [mainImage, ...images.slice(1)] : images,

      // Mock author for consistency
      author: {
        id: {
          uuid: 'viator-partner',
          type: 'user',
        },
        type: 'user',
        attributes: {
          profile: {
            displayName: 'Viator Experience',
            abbreviatedName: 'Viator',
          },
        },
      },
      relationships: {
        author: {
          data: {
            id: {
              uuid: 'viator-partner',
              type: 'user',
            },
            type: 'user',
          },
        },
        images: {
          data: images.map(img => ({
            id: img.id,
            type: 'image',
          })),
        },
      },
    };

    return result;
  } catch (error) {
    console.error('💥 Error transforming DynamoDB item:', error);
    console.error('💥 Problem item:', dynamoItem);
    throw error;
  }
};

/**
 * Enhanced fetch with review prioritization parameters
 */
export const fetchDynamoDBListings = async (searchParams = {}) => {
  try {
    // Enhanced search parameters with review prioritization
    const enhancedParams = {
      ...searchParams,
      sort: 'partner_priority', // Use our enhanced sorting
      prioritizeReviews: 'true',
      minRating: '3.0', // Only show experiences with decent ratings
    };

    // Apply high-review filter for general searches
    if (!searchParams.keywords && !searchParams.destination) {
      enhancedParams.minReviews = '25'; // General searches show high-quality experiences
    } else if (searchParams.keywords) {
      enhancedParams.minReviews = '10'; // Keyword searches show good experiences
    }


    const response = await apiSearchDynamoDBListings(enhancedParams);

    if (!response) {
      return {
        data: [],
        meta: { totalItems: 0, totalPages: 0, page: 1, perPage: 0 },
      };
    }

    if (!response.items) {
      return {
        data: [],
        meta: { totalItems: 0, totalPages: 0, page: 1, perPage: 0 },
      };
    }

    if (response.items.length === 0) {
      return {
        data: [],
        meta: {
          totalItems: response.totalItems || 0,
          totalPages: response.totalPages || 0,
          page: response.page || 1,
          perPage: response.perPage || 0,
        },
      };
    }
    
    const transformedListings = [];

    for (let i = 0; i < response.items.length; i++) {
      const item = response.items[i];

      try {
        const transformed = transformDynamoDBToSharetribeListing(item);
        transformedListings.push(transformed);

        // Log coordinate status for debugging
        const hasGeo = !!transformed.attributes.geolocation;

      } catch (transformError) {
        console.error(`❌ Error transforming partner experience ${i + 1}:`, transformError);
        console.error('❌ Problem item:', item);
        // Continue with other items instead of failing completely
      }
    }

    const result = {
      data: transformedListings,
      meta: {
        totalItems: response.totalItems || transformedListings.length,
        totalPages: response.totalPages || 1,
        page: response.page || 1,
        perPage: response.perPage || transformedListings.length,
        appliedFilters: response.appliedFilters,
      },
    };

    return result;
  } catch (error) {
    return {
      data: [],
      meta: {
        totalItems: 0,
        totalPages: 0,
        page: 1,
        perPage: 0,
      },
    };
  }
};

/**
 * Filter DynamoDB listings based on search criteria (client-side fallback)
 */
export const filterDynamoDBListings = (listings, searchParams) => {
  let filtered = [...listings];

  // Keyword search
  if (searchParams.keywords) {
    const keywords = searchParams.keywords.toLowerCase();
    filtered = filtered.filter(
      listing =>
        listing.attributes.title.toLowerCase().includes(keywords) ||
        listing.attributes.description.toLowerCase().includes(keywords) ||
        listing.attributes.publicData.location.destination?.toLowerCase().includes(keywords)
    );
  }

  // Review count filtering
  if (searchParams.minReviews) {
    const minReviews = parseInt(searchParams.minReviews);
    filtered = filtered.filter(
      listing => (listing.attributes.publicData.reviewCount || 0) >= minReviews
    );
  }

  // Rating filtering
  if (searchParams.minRating) {
    const minRating = parseFloat(searchParams.minRating);
    filtered = filtered.filter(listing => (listing.attributes.publicData.rating || 0) >= minRating);
  }

  // Location bounds filtering
  if (searchParams.bounds && searchParams.bounds.ne && searchParams.bounds.sw) {
    filtered = filtered.filter(listing => {
      const geolocation = listing.attributes.geolocation;
      if (!geolocation) return false;

      const { ne, sw } = searchParams.bounds;
      return (
        geolocation.lat >= sw.lat &&
        geolocation.lat <= ne.lat &&
        geolocation.lng >= sw.lng &&
        geolocation.lng <= ne.lng
      );
    });
  }

  // Price filtering
  if (searchParams.price) {
    const [minPrice, maxPrice] = searchParams.price.split(',').map(p => parseInt(p));
    filtered = filtered.filter(listing => {
      const price = listing.attributes.price.amount;
      return price >= minPrice && price <= maxPrice;
    });
  }

  return filtered;
};

/**
 * Check DynamoDB API health
 */
export const checkDynamoDBHealth = async () => {
  try {
    const response = await checkDynamoDBHealthAPI();
    return response;
  } catch (error) {
    console.error('DynamoDB health check failed:', error);
    return {
      status: 'unhealthy',
      error: error.message,
    };
  }
};
